import type { HttpQueryParam } from "@/core/interfaces";

interface FilterColumn {
  key: string;
  label: string;
  options?: { value: string; label: string }[];
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Linhas do resumo no arquivo: busca, período e cada filtro do painel, com o rótulo da tela (não o identificador).
 * O rótulo vem da opção carregada ou, quando ela saiu das opções (obra fora do período), de `rotulosVistos`; só o GUID
 * que não tem rótulo em nenhum dos dois fica de fora. Sem nenhum critério, a lista fica vazia.
 */
export function describeReportFilters(
  search: string | undefined,
  columns: FilterColumn[],
  value: Record<string, string[] | undefined>,
  extras?: { periodo?: string },
  rotulosVistos?: ReadonlyMap<string, string>,
): string[] {
  const lines: string[] = [];
  const term = search?.trim();
  if (term) lines.push(`Busca: ${term}`);

  const periodo = extras?.periodo?.trim();
  if (periodo) lines.push(`Período: ${periodo}`);

  for (const column of columns) {
    const selected = value[column.key] ?? [];
    if (selected.length === 0) continue;
    const labels = selected
      .map(
        (item) =>
          column.options?.find((option) => option.value === item)?.label ??
          rotulosVistos?.get(item) ??
          item,
      )
      .filter((label) => !GUID_PATTERN.test(label));
    if (labels.length === 0) continue;
    lines.push(`${column.label}: ${labels.join(", ")}`);
  }

  return lines;
}

/** Traduz as chaves do painel para os parâmetros que o core aplica na exportação. */
export function panelQuery(
  value: Record<string, string[] | undefined>,
  spec: { key: string; param: string; multiple: boolean }[],
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};

  for (const item of spec) {
    const selected = (value[item.key] ?? []).map((entry) => entry.trim()).filter(Boolean);
    if (selected.length === 0) continue;
    params[item.param] = item.multiple ? selected : (selected[0] ?? "");
  }

  return params;
}

export function appendReportQuery(
  params: Record<string, HttpQueryParam>,
  filtros: string[] | undefined,
  painel?: Record<string, string | string[]>,
): void {
  const lines = (filtros ?? []).map((line) => line.trim()).filter(Boolean);
  if (lines.length > 0) params.filtros = lines;
  for (const [key, entry] of Object.entries(painel ?? {})) {
    if (typeof entry === "string" && entry.length > 0) params[key] = entry;
    if (Array.isArray(entry) && entry.length > 0) params[key] = entry;
  }
}
